import { Input } from "@/components/ui/input";
import { SrIcon } from "@/components/ui/SrIcon";
import { cn } from "@/lib/utils";
import type { DBDepartment, DBEquipmentCategory } from "@/lib/db-types";
import { DepartmentToggle } from "./DepartmentToggle";

interface EquipmentFiltersProps {
  departments: DBDepartment[];
  department: string;
  onDepartmentChange: (value: string) => void;
  search: string;
  onSearchChange: (value: string) => void;
  categories: DBEquipmentCategory[];
  categoryId: string | null;
  onCategoryChange: (id: string | null) => void;
  countsByCategory: Map<string, number>;
  total: number;
}

function Chip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
        active ? "border-primary bg-primary text-primary-foreground" : "border-border hover:bg-muted",
      )}
    >
      {children}
    </button>
  );
}

/** Avdelingsbryter, søk og kategorichips med antall (skjerm A). */
export function EquipmentFilters(props: EquipmentFiltersProps) {
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <DepartmentToggle departments={props.departments} value={props.department} onChange={props.onDepartmentChange} />
        <div className="relative flex-1 min-w-[14rem] max-w-md">
          <SrIcon name="search" className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={props.search}
            onChange={(e) => props.onSearchChange(e.target.value)}
            placeholder="Søk på navn eller serienummer"
            className="pl-8"
            aria-label="Søk i utstyr"
          />
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5" role="group" aria-label="Kategori">
        <Chip active={props.categoryId === null} onClick={() => props.onCategoryChange(null)}>
          Alle <span className="opacity-70">{props.total}</span>
        </Chip>
        {props.categories.map((c) => (
          <Chip key={c.id} active={props.categoryId === c.id} onClick={() => props.onCategoryChange(c.id)}>
            {c.name} <span className="opacity-70">{props.countsByCategory.get(c.id) ?? 0}</span>
          </Chip>
        ))}
      </div>
    </div>
  );
}
